import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import type { NamedValue, VueMessageType } from 'vue-i18n';
import { useI18n } from './useI18n';

/** Resolves a translation array; `values` interpolate every string it contains. */
export function useI18nArray<T>(translationKey: MaybeRefOrGetter<string>, values?: MaybeRefOrGetter<NamedValue>): ComputedRef<T[]> {
  const { tm, rt } = useI18n();

  function isResourceNode(content: object): content is Exclude<VueMessageType, string> {
    // Check for both development AST format ('loc' and 'type')
    // and production compiled format ('t' for type)
    return ('loc' in content && 'type' in content) || ('t' in content);
  }

  function resolve(content: Parameters<typeof rt>[0]): string {
    return rt(content, toValue(values) ?? {});
  }

  function processContent(content: unknown): unknown {
    if (Array.isArray(content)) {
      return content.map(processContent);
    }

    if (typeof content === 'object' && content !== null) {
      // If the object looks like a vue-i18n AST node, resolve it
      if (isResourceNode(content)) {
        return resolve(content);
      }

      const result: Record<string, unknown> = {};
      for (const key in content) {
        result[key] = processContent((content as Record<string, unknown>)[key]);
      }
      return result;
    }

    if (typeof content === 'string') {
      return resolve(content);
    }

    return content;
  }

  return computed(() => {
    const keyValue = toValue(translationKey);
    const raw = tm(keyValue);
    return Array.isArray(raw) ? (raw.map(processContent) as T[]) : [];
  });
}
